import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'
import { SearchIcon, EditIcon, DeleteIcon } from '../Icons'
import { Plus, BookOpen, MoreVertical, ChevronDown, ChevronUp } from 'lucide-react'
import Modal from '../ui/Modal'
import ConfirmModal from '../ui/ConfirmModal'
import AddCourse from './AddCourse'
import EditCourse from './EditCourse'

function CourseList() {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [entriesCount, setEntriesCount] = useState(12)
  const [currentPage, setCurrentPage] = useState(1)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [courseToDelete, setCourseToDelete] = useState(null)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedCourseId, setSelectedCourseId] = useState(null)
  const [openMenuId, setOpenMenuId] = useState(null)
  const [expandedDescriptions, setExpandedDescriptions] = useState({})
  const menuRefs = useRef({})
  const navigate = useNavigate()
  const api = apiService()

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getAllCourses()
        setIsLoading(false)
      } catch (error) {
        console.error('Error loading data:', error)
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const getAllCourses = async () => {
    try {
      const response = await api.get('courses')
      if (response.status === 200) {
        setData(response.data.courses || response.data || [])
      }
    } catch (error) {
      console.error('Error fetching courses:', error)
      toast.error('Failed to fetch courses')
    }
  }

  const deleteCourse = async (id) => {
    try {
      const response = await api.del(`courses/${id}`)
      toast.success('Course deleted successfully')
      await getAllCourses()
      setDeleteModalOpen(false)
      setCourseToDelete(null)
    } catch (error) {
      console.error('Error deleting course:', error)
      toast.error('Failed to delete course')
    }
  }

  const handleDeleteClick = (id) => {
    setCourseToDelete(id)
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    if (courseToDelete) {
      deleteCourse(courseToDelete)
    }
  }

  // Filtered and paginated data
  const filteredData = data.filter((item) => {
    return (
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })

  const indexOfLastEntry = currentPage * entriesCount
  const indexOfFirstEntry = indexOfLastEntry - entriesCount
  const paginatedData = filteredData.slice(indexOfFirstEntry, indexOfLastEntry)
  const totalPages = Math.ceil(filteredData.length / entriesCount)

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber)
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [entriesCount, searchTerm])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && menuRefs.current[openMenuId]) {
        if (!menuRefs.current[openMenuId].contains(event.target)) {
          setOpenMenuId(null)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openMenuId])

  const handleOpenAddModal = () => setAddModalOpen(true)
  const handleCloseAddModal = () => {
    setAddModalOpen(false)
    getAllCourses()
  }

  const handleOpenEditModal = (id) => {
    setSelectedCourseId(id)
    setEditModalOpen(true)
    setOpenMenuId(null) // Close menu when opening edit modal
  }
  const handleCloseEditModal = () => {
    setEditModalOpen(false)
    setSelectedCourseId(null)
    getAllCourses()
  }

  const toggleMenu = (courseId, e) => {
    e.stopPropagation()
    setOpenMenuId(openMenuId === courseId ? null : courseId)
  }

  const handleMenuEdit = (courseId, e) => {
    e.stopPropagation()
    handleOpenEditModal(courseId)
  }

  const handleMenuDelete = (courseId, e) => {
    e.stopPropagation()
    handleDeleteClick(courseId)
    setOpenMenuId(null)
  }

  const toggleDescription = (courseId, e) => {
    e.stopPropagation()
    setExpandedDescriptions((prev) => ({
      ...prev,
      [courseId]: !prev[courseId],
    }))
  }

  return (
    <section className="bg-white border rounded-[10px] border-[#F0F0F0]">
      {/* Header Controls */}
      <div className="justify-between items-center border border-[#F0F0F0] flex w-full gap-[40px_100px] font-medium leading-[1.2] flex-wrap bg-white px-5 py-4 rounded-t-[10px]">
        <h2 className="text-lg text-[#030303] font-bold">Course Management</h2>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[250px]">
              <div className="flex items-center gap-1 border border-[#F0F0F0] bg-white px-3 py-2 rounded-md w-full focus-within:border-[#DE3C7D]">
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Search courses..."
                  className="text-[#535862] bg-transparent outline-none border-none w-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleOpenAddModal}
              className="flex items-center gap-2 px-4 py-2 bg-[#DE3C7D] text-white rounded-md hover:bg-[#c8356f] transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Course</span>
            </button>
          </div>
      </div>

      {/* Course Cards Grid */}
      <div className="p-5">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="bg-white border border-[#F0F0F0] rounded-lg overflow-hidden animate-pulse"
              >
                <div className="h-48 bg-gray-200"></div>
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : paginatedData.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedData.map((item) => (
              <div
                key={item._id}
                className="bg-white border border-[#F0F0F0] rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => navigate(`/course-management/${item._id}`)}
              >
                {/* Course Thumbnail */}
                <div className="relative h-48 bg-gradient-to-br from-[#DE3C7D] to-[#c8356f] overflow-hidden">
                  {item.thumbnail ? (
                    <img
                      src={item.thumbnail.startsWith('/uploads/') ? `${process.env.REACT_APP_BASE_URL?.replace(/\/$/, '')}${item.thumbnail}` : item.thumbnail}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextElementSibling.style.display = 'flex'
                      }}
                    />
                  ) : null}
                  {(!item.thumbnail || item.thumbnail === '') && (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-16 h-16 text-white opacity-50" />
                    </div>
                  )}
                  {/* Three-dots menu at top-right */}
                  <div className="absolute top-3 right-3" ref={(el) => (menuRefs.current[item._id] = el)}>
                    <button
                      onClick={(e) => toggleMenu(item._id, e)}
                      className="p-2 bg-white/90 backdrop-blur-sm rounded-md hover:bg-white transition-colors shadow-sm"
                      title="More options"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {openMenuId === item._id && (
                      <div className="absolute top-10 right-0 bg-white rounded-md shadow-lg border border-[#F0F0F0] z-10 min-w-[120px]">
                        <button
                          onClick={(e) => handleMenuEdit(item._id, e)}
                          className="w-full px-4 py-2 text-left text-sm text-[#030303] hover:bg-neutral-50 flex items-center gap-2 transition-colors"
                        >
                          <EditIcon className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={(e) => handleMenuDelete(item._id, e)}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                        >
                          <DeleteIcon className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Lesson count badge at bottom-right */}
                  <div className="absolute bottom-3 right-3">
                    <div className="bg-black/70 backdrop-blur-sm rounded-md px-3 py-1.5 flex items-center gap-1.5">
                      <BookOpen className="w-3.5 h-3.5 text-white" />
                      <span className="text-white text-xs font-medium">
                        {item.lessons?.length || 0} {item.lessons?.length === 1 ? 'lesson' : 'lessons'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Course Info */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-[#030303] mb-2 line-clamp-2">
                    {item.title}
                  </h3>
                  <div className="mb-2">
                    <p className={`text-sm text-[#535862] ${!expandedDescriptions[item._id] ? 'line-clamp-2' : ''}`}>
                      {item.description || 'No description available'}
                    </p>
                    {item.description && item.description.length > 100 && (
                      <button
                        onClick={(e) => toggleDescription(item._id, e)}
                        className="text-xs text-[#DE3C7D] hover:text-[#c8356f] font-medium mt-1 flex items-center gap-1 transition-colors"
                      >
                        {expandedDescriptions[item._id] ? (
                          <>
                            <span>Show less</span>
                            <ChevronUp className="w-3 h-3" />
                          </>
                        ) : (
                          <>
                            <span>Show more</span>
                            <ChevronDown className="w-3 h-3" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[#535862]">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">No courses found</p>
            <p className="text-sm mb-4">
              {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first course'}
            </p>
            {!searchTerm && (
              <button
                onClick={handleOpenAddModal}
                className="px-4 py-2 bg-[#DE3C7D] text-white rounded-md hover:bg-[#c8356f] transition-colors"
              >
                Add First Course
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center px-5 py-4 border-t border-[#F0F0F0]">
          <div className="text-sm text-[#535862]">
            Showing {indexOfFirstEntry + 1} to{' '}
            {Math.min(indexOfLastEntry, filteredData.length)} of{' '}
            {filteredData.length} courses
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-[#F0F0F0] rounded-md text-sm text-[#535862] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50 transition-colors"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-[#030303]">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-[#F0F0F0] rounded-md text-sm text-[#535862] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <Modal
        isOpen={addModalOpen}
        onClose={handleCloseAddModal}
        title="Add Course"
      >
        <AddCourse onClose={handleCloseAddModal} />
      </Modal>

      <Modal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        title="Edit Course"
      >
        <EditCourse id={selectedCourseId} onClose={handleCloseEditModal} />
      </Modal>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false)
          setCourseToDelete(null)
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Course"
        message="Are you sure you want to delete this course? This action cannot be undone."
      />
    </section>
  )
}

export default CourseList

